import { useEffect } from 'react';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { BASE_TOOL_COMMON_PROPS } from '@/tools/tool.constants';
import { type ActionsFactory, createStore, createStoreInstance } from '@seeqdev/utils';
import type { BaseToolPersistedState } from '@/tools/tool.types';

export type ItemPropertiesState = BaseToolPersistedState & {
  /** ID of the item being duplicated, or an empty string. */
  duplicatingItemId: string;
};

export type ItemPropertiesDehydratedState = Pick<
  ItemPropertiesState,
  'advancedParametersCollapsed' | 'duplicatingItemId'
>;

type ItemPropertiesActions = {
  /** Shows or hides the Advanced section. */
  setAdvancedParametersCollapsed: (collapsed: boolean) => void;
  /** Sets the copy source, or clears it when the ID is empty. */
  setDuplicatingItemId: (id: string) => void;
  /** Resets the tool state to its defaults. */
  reset: () => void;
};

const initialState: ItemPropertiesState = Object.freeze({
  ...BASE_TOOL_COMMON_PROPS,
  duplicatingItemId: '',
});

export const dehydrateItemPropertiesState = (state: ItemPropertiesState): ItemPropertiesDehydratedState => ({
  advancedParametersCollapsed: state.advancedParametersCollapsed,
  duplicatingItemId: state.duplicatingItemId,
});

const itemPropertiesActionsFactory: ActionsFactory<ItemPropertiesState, ItemPropertiesActions> = (
  set,
  _get,
  resetState,
) => ({
  setAdvancedParametersCollapsed: (collapsed) =>
    set((draft) => {
      draft.advancedParametersCollapsed = collapsed;
    }),
  setDuplicatingItemId: (id) =>
    set((draft) => {
      draft.duplicatingItemId = id;
    }),
  reset: resetState,
});

const STORE_NAME = 'ItemProperties';
const [ItemPropertiesStoreProvider, useItemPropertiesStoreInstance, useItemPropertiesStoreSelector] = createStore(
  STORE_NAME,
  initialState,
  itemPropertiesActionsFactory,
);

export { ItemPropertiesStoreProvider };

export const useItemPropertiesAdvancedParametersCollapsed = () =>
  useItemPropertiesStoreSelector((state) => state.advancedParametersCollapsed);
export const useItemPropertiesDuplicatingItemId = () =>
  useItemPropertiesStoreSelector((state) => state.duplicatingItemId);
export const useItemPropertiesActions = () => useItemPropertiesStoreInstance().actions;

/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores.
 * DO NOT use in new code.
 */
export function legacyCreateItemPropertiesStore() {
  return createStoreInstance(initialState, itemPropertiesActionsFactory, { storeName: STORE_NAME });
}

export const usePersistItemPropertiesStore = () => {
  const register = useRegisterPersistedStore();
  const store = useItemPropertiesStoreInstance();
  useEffect(
    () =>
      register({
        storeName: 'sqItemPropertiesStore',
        persistenceLevel: 'WORKSHEET',
        store,
        dehydrate: dehydrateItemPropertiesState,
      }),
    [register, store],
  );
};
